import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, Minus, Plus, ArrowRight, ArrowLeft, Gift, Truck } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../ui/dialog";
import { REVIVE_FLAVOURS } from "../brand/flavours";
import { computeCart, REVIVE_UNIT } from "@/lib/pricing";
import { CheckoutSupportStrip } from "./CheckoutSupportStrip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const UNIT_PRICE = 49.95;
const BUNDLE_TRIGGER = 4; // Buy 4, get 1 free per 4 kg purchased

const EMIRATES = [
  { value: "abu-dhabi", label: "Abu Dhabi" },
  { value: "dubai", label: "Dubai" },
  { value: "sharjah", label: "Sharjah" },
  { value: "ajman", label: "Ajman" },
  { value: "umm-al-quwain", label: "Umm Al Quwain" },
  { value: "ras-al-khaimah", label: "Ras Al Khaimah" },
  { value: "fujairah", label: "Fujairah" },
];

const emptyAddress = {
  name: "",
  phone: "",
  email: "",
  emirate: "dubai",
  city: "",
  address_line1: "",
  address_line2: "",
  notes: "",
};

export const OryvitaBuyDialog = ({ open, onOpenChange }) => {
  const [step, setStep] = useState(1); // 1=qty, 2=cross-sell REVIVE, 3=shipping
  const [quantity, setQuantity] = useState(1);
  const [reviveFlavour, setReviveFlavour] = useState(REVIVE_FLAVOURS[0].name);
  const [reviveBoxes, setReviveBoxes] = useState(0);
  const [address, setAddress] = useState(emptyAddress);
  const [loading, setLoading] = useState(false);

  const bonusKg = Math.floor(quantity / BUNDLE_TRIGGER);
  const shippedKg = quantity + bonusKg;
  const cart = computeCart(reviveBoxes, quantity);
  const subtotal = cart.subtotal;
  const delivery = cart.delivery;
  const total = cart.total;

  const bump = (delta) => setQuantity((q) => Math.min(20, Math.max(1, q + delta)));

  const onAddrChange = (e) =>
    setAddress((a) => ({ ...a, [e.target.name]: e.target.value }));

  const handleOpenChange = (v) => {
    if (!v) {
      // reset when dialog closes
      setStep(1);
    }
    onOpenChange(v);
  };

  const goToDelivery = () => setStep(2);

  const validateAddress = () => {
    const required = ["name", "phone", "email", "emirate", "city", "address_line1"];
    for (const key of required) {
      if (!String(address[key] || "").trim()) return `Please fill in ${key.replace("_", " ")}.`;
    }
    // very light email/phone shape check
    if (!/^\S+@\S+\.\S+$/.test(address.email)) return "Please enter a valid email address.";
    return null;
  };

  const submit = async () => {
    const err = validateAddress();
    if (err) {
      toast.error(err);
      return;
    }
    setLoading(true);
    try {
      const items = [{ product_id: "shirataki-rice", quantity }];
      if (reviveBoxes > 0) items.push({ product_id: "field-salts", flavor: reviveFlavour, quantity: reviveBoxes });
      const { data } = await axios.post(`${API}/checkout`, {
        items,
        shipping: {
          name: address.name.trim(),
          phone: address.phone.trim(),
          email: address.email.trim(),
          emirate: address.emirate,
          city: address.city.trim(),
          address_line1: address.address_line1.trim(),
          address_line2: address.address_line2.trim() || undefined,
          notes: address.notes.trim() || undefined,
        },
      });
      if (data?.redirect_url) {
        window.location.assign(data.redirect_url);
      } else {
        toast.error("Could not start checkout — no redirect URL returned.");
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Checkout is not available right now. Please try again later.",
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        data-testid="oryvita-buy-dialog"
        className="max-w-lg w-[calc(100vw-2rem)] bg-bone text-ink border-hairline p-0 overflow-hidden"
      >
        {/* Header band */}
        <div className="bg-ink text-bone px-6 md:px-8 py-6">
          <DialogHeader className="space-y-2 text-left">
            <div className="overline text-terracotta">
              Oryvita · Dry Shirataki Rice · Step {step} of 3
            </div>
            <DialogTitle className="font-display font-black text-3xl md:text-4xl tracking-tight">
              {step === 1 ? (
                <>
                  How many kilos
                  <span className="font-wordmark italic font-semibold text-bone/80 ml-2">
                    today?
                  </span>
                </>
              ) : step === 2 ? (
                <>Before you go —</>
              ) : (
                <>
                  Where should we
                  <span className="font-wordmark italic font-semibold text-bone/80 ml-2">
                    send it?
                  </span>
                </>
              )}
            </DialogTitle>
            <DialogDescription className="text-bone/70 text-sm">
              AED 49.95 per kg · Plant-based · Gluten-free · Sugar-free · 41 kcal / serving.
            </DialogDescription>
          </DialogHeader>
        </div>

        {/* Body */}
        <div className="px-6 md:px-8 py-7 max-h-[calc(100vh-260px)] overflow-y-auto">
          {step === 1 ? (
            <>
              {/* Bundle offer banner */}
              <div
                data-testid="oryvita-bundle-banner"
                className={`flex items-start gap-3 border rounded-sm px-4 py-3 mb-6 transition-colors ${
                  bonusKg > 0
                    ? "border-terracotta bg-terracotta/8"
                    : "border-hairline bg-surface"
                }`}
              >
                <Gift
                  className={`w-5 h-5 mt-0.5 shrink-0 ${
                    bonusKg > 0 ? "text-terracotta" : "text-inkmute"
                  }`}
                  strokeWidth={1.75}
                />
                <div>
                  <div className="overline text-terracotta">Bundle offers available</div>
                  <p className="text-sm text-ink/85 mt-1 leading-snug">
                    Buy 4 kg and get 1 kg free — plus free delivery.
                  </p>
                  {bonusKg > 0 && (
                    <p
                      className="text-sm font-display font-bold text-terracotta mt-1"
                      data-testid="oryvita-bundle-applied"
                    >
                      ✦ Bonus applied — {bonusKg} kg free will be added to your delivery.
                    </p>
                  )}
                </div>
              </div>

              {/* Stepper + total */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <div className="overline text-inkmute mb-3">Quantity (kg)</div>
                  <div
                    data-testid="oryvita-quantity-stepper"
                    className="inline-flex items-center border border-hairline rounded-sm bg-surface"
                  >
                    <button
                      type="button"
                      onClick={() => bump(-1)}
                      disabled={quantity <= 1}
                      data-testid="oryvita-qty-decrease"
                      className="px-3 py-3 hover:bg-surfacealt disabled:opacity-30 transition-colors"
                      aria-label="Decrease quantity"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span
                      data-testid="oryvita-qty-value"
                      className="font-display font-bold text-lg w-12 text-center"
                    >
                      {quantity}
                    </span>
                    <button
                      type="button"
                      onClick={() => bump(1)}
                      disabled={quantity >= 20}
                      data-testid="oryvita-qty-increase"
                      className="px-3 py-3 hover:bg-surfacealt disabled:opacity-30 transition-colors"
                      aria-label="Increase quantity"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <p className="text-xs text-inkmute mt-2" data-testid="oryvita-shipped">
                    Ships: {shippedKg} kg
                    {bonusKg > 0 ? (
                      <span className="text-terracotta font-semibold">
                        {" "}
                        ({quantity} paid + {bonusKg} free)
                      </span>
                    ) : null}
                  </p>
                </div>

                <div className="sm:text-right">
                  <div className="overline text-inkmute mb-3">Subtotal</div>
                  <div
                    className="font-display font-black text-3xl md:text-4xl text-ink leading-none"
                    data-testid="oryvita-subtotal"
                  >
                    AED {subtotal.toFixed(2)}
                  </div>
                  <p className="text-[11px] text-inkmute mt-1 uppercase tracking-widest font-display font-semibold">
                    Incl VAT
                  </p>
                  <p className="text-xs text-inkmute mt-2">
                    {quantity} × AED {UNIT_PRICE.toFixed(2)} / kg
                  </p>
                </div>
              </div>

              {/* Delivery info */}
              <div className="mt-7 pt-6 border-t border-hairline">
                <div className="flex items-center gap-2 mb-3">
                  <Truck className="w-4 h-4 text-terracotta" strokeWidth={1.75} />
                  <span className="overline text-ink">Delivery</span>
                </div>
                <ul className="space-y-2 text-sm text-ink/80 leading-relaxed">
                  <li className="flex gap-2">
                    <span className="text-terracotta shrink-0">·</span>
                    <span>
                      <span className="font-display font-semibold text-ink">
                        UAE-wide delivery
                      </span>{" "}
                      within 1–2 business days from Dubai.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-terracotta shrink-0">·</span>
                    <span>
                      <span className="font-display font-semibold text-ink">
                        Free delivery
                      </span>{" "}
                      on orders over AED 200 · AED 15 delivery fee for orders below.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-terracotta shrink-0">·</span>
                    <span>
                      Order tracking sent via SMS &amp; email once your parcel is
                      dispatched.
                    </span>
                  </li>
                  <li className="flex gap-2">
                    <span className="text-terracotta shrink-0">·</span>
                    <span>
                      Questions? WhatsApp{" "}
                      <a
                        href="https://wa.me/971501227625"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-display font-semibold text-terracotta link-underline"
                      >
                        +971 50 122 7625
                      </a>
                      .
                    </span>
                  </li>
                </ul>
              </div>
            </>
          ) : step === 2 ? (
            <div data-testid="oryvita-crosssell" className="space-y-5">
              <p className="text-ink/80 text-base leading-relaxed">
                <span className="font-wordmark italic text-terracotta">…have you tried REVIVE?</span> Our daily electrolyte range — a smart pairing with Oryvita.
              </p>
              <div className="border border-hairline bg-surface rounded-sm p-4 md:p-5">
                <div className="flex items-baseline justify-between mb-3">
                  <div>
                    <div className="font-display font-bold text-ink text-lg">REVIVE — Daily Electrolytes</div>
                    <div className="text-xs text-inkmute mt-1">AED {REVIVE_UNIT} / box · 30 sachets · 2-box bundle AED 300 · 3+ boxes 10% off</div>
                  </div>
                  <div className="inline-flex items-center border border-hairline rounded-sm bg-bone">
                    <button type="button" onClick={() => setReviveBoxes((v) => Math.max(0, v - 1))} disabled={reviveBoxes <= 0} data-testid="crosssell-revive-dec" className="px-2 py-2 hover:bg-surfacealt disabled:opacity-30"><Minus className="w-3.5 h-3.5" /></button>
                    <span data-testid="crosssell-revive-qty" className="font-display font-bold text-sm w-10 text-center">{reviveBoxes}</span>
                    <button type="button" onClick={() => setReviveBoxes((v) => Math.min(20, v + 1))} data-testid="crosssell-revive-inc" className="px-2 py-2 hover:bg-surfacealt"><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                </div>
                {reviveBoxes > 0 ? (
                  <div className="mt-2">
                    <label className="overline text-inkmute block mb-2">Flavour</label>
                    <select value={reviveFlavour} onChange={(e) => setReviveFlavour(e.target.value)} data-testid="crosssell-revive-flavour" className="w-full bg-bone border border-hairline outline-none py-2.5 px-3 text-ink text-sm rounded-sm">
                      {REVIVE_FLAVOURS.map((f) => (<option key={f.name} value={f.name}>{f.name}{f.badge ? " · NEW" : ""}</option>))}
                    </select>
                    <div className="text-sm text-terracotta font-display font-semibold mt-3">✦ Added {reviveBoxes} box{reviveBoxes > 1 ? "es" : ""} — AED {cart.reviveSubtotal.toFixed(2)}{cart.reviveSavings > 0 ? ` (saved AED ${cart.reviveSavings.toFixed(2)})` : ""}</div>
                  </div>
                ) : (
                  <button type="button" onClick={() => setReviveBoxes(1)} data-testid="crosssell-revive-add-1" className="text-sm font-display font-semibold text-terracotta hover:text-ink link-underline">Add 1 box for AED {REVIVE_UNIT}</button>
                )}
              </div>
              <div className="text-xs text-inkmute">No thanks? Just click Continue — we'll pack Oryvita only.</div>
            </div>
          ) : (
            <div data-testid="oryvita-shipping-form" className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <Field
                  label="Full name"
                  name="name"
                  value={address.name}
                  onChange={onAddrChange}
                  placeholder="Jane Smith"
                  testId="shipping-name"
                />
                <Field
                  label="Phone"
                  name="phone"
                  value={address.phone}
                  onChange={onAddrChange}
                  placeholder="+971 50 000 0000"
                  testId="shipping-phone"
                />
              </div>
              <Field
                label="Email"
                name="email"
                type="email"
                value={address.email}
                onChange={onAddrChange}
                placeholder="you@domain.com"
                testId="shipping-email"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="overline text-inkmute block mb-2">Emirate</label>
                  <select
                    name="emirate"
                    value={address.emirate}
                    onChange={onAddrChange}
                    data-testid="shipping-emirate"
                    className="w-full bg-surface border border-hairline focus:border-ink outline-none py-3 px-3 text-ink font-body text-base rounded-sm"
                  >
                    {EMIRATES.map((e) => (
                      <option key={e.value} value={e.value}>
                        {e.label}
                      </option>
                    ))}
                  </select>
                </div>
                <Field
                  label="City / Area"
                  name="city"
                  value={address.city}
                  onChange={onAddrChange}
                  placeholder="e.g. Business Bay"
                  testId="shipping-city"
                />
              </div>
              <Field
                label="Building / Villa / Street"
                name="address_line1"
                value={address.address_line1}
                onChange={onAddrChange}
                placeholder="Building name, villa no., street"
                testId="shipping-address1"
              />
              <Field
                label="Apartment / Floor (optional)"
                name="address_line2"
                value={address.address_line2}
                onChange={onAddrChange}
                placeholder="Apt / floor / unit"
                testId="shipping-address2"
              />
              <div>
                <label className="overline text-inkmute block mb-2">
                  Delivery notes (optional)
                </label>
                <textarea
                  name="notes"
                  rows={2}
                  value={address.notes}
                  onChange={onAddrChange}
                  data-testid="shipping-notes"
                  placeholder="Gate code, landmark, best time to deliver…"
                  className="w-full bg-surface border border-hairline focus:border-ink outline-none py-3 px-3 text-ink font-body text-base rounded-sm resize-none"
                />
              </div>

              {/* Order summary */}
              <div className="border-t border-hairline pt-5 mt-6" data-testid="order-summary">
                <div className="flex justify-between text-sm mb-2">
                  <span className="text-inkmute">
                    Oryvita × {quantity} kg
                    {bonusKg > 0 && (
                      <span className="text-terracotta font-semibold">
                        {" "}
                        (+ {bonusKg} kg free)
                      </span>
                    )}
                  </span>
                  <span className="text-ink font-semibold">
                    AED {cart.oryvitaSubtotal.toFixed(2)}
                  </span>
                </div>
                {reviveBoxes > 0 && (
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-inkmute">REVIVE × {reviveBoxes} {reviveBoxes>=2 && <span className="text-terracotta font-semibold">· bundle</span>}</span>
                    <span className="text-ink font-semibold">AED {cart.reviveSubtotal.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm mb-3">
                  <span className="text-inkmute">Delivery</span>
                  <span
                    data-testid="delivery-fee"
                    className={`font-semibold ${
                      delivery === 0 ? "text-terracotta" : "text-ink"
                    }`}
                  >
                    {delivery === 0 ? "FREE" : `AED ${delivery.toFixed(2)}`}
                  </span>
                </div>
                <div className="flex justify-between items-baseline pt-3 border-t border-hairline">
                  <span className="overline text-ink">Total</span>
                  <span
                    data-testid="oryvita-total"
                    className="font-display font-black text-2xl text-ink"
                  >
                    AED {total.toFixed(2)}
                  </span>
                </div>
                <p className="text-[11px] text-inkmute mt-1 uppercase tracking-widest font-display font-semibold text-right">
                  Incl VAT
                </p>
              </div>
            </div>
          )}
        </div>

        <CheckoutSupportStrip />

        {/* Footer CTA */}
        <div className="bg-surfacealt border-t border-hairline px-6 md:px-8 py-5 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          {step === 1 ? (
            <>
              <div className="text-xs text-inkmute">
                Secure UAE checkout · Powered by Ziina
              </div>
              <button
                type="button"
                onClick={() => setStep(2)}
                data-testid="oryvita-continue-crosssell"
                className="group inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-ink transition-colors duration-300 text-white px-6 py-3.5 rounded-sm font-display font-semibold text-sm"
              >
                Continue
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </>
          ) : step === 2 ? (
            <>
              <button
                type="button"
                onClick={() => setStep(1)}
                data-testid="oryvita-back-to-qty"
                className="inline-flex items-center gap-2 text-ink hover:text-terracotta overline link-underline"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <button
                type="button"
                onClick={() => setStep(3)}
                data-testid="oryvita-continue-delivery"
                className="group inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-ink transition-colors duration-300 text-white px-6 py-3.5 rounded-sm font-display font-semibold text-sm"
              >
                Continue to delivery
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setStep(2)}
                data-testid="oryvita-back"
                className="inline-flex items-center gap-2 text-ink hover:text-terracotta overline link-underline"
              >
                <ArrowLeft className="w-4 h-4" />
                Back
              </button>
              <button
                type="button"
                onClick={submit}
                disabled={loading}
                data-testid="oryvita-checkout-cta"
                className="group inline-flex items-center justify-center gap-2 bg-terracotta hover:bg-ink transition-colors duration-300 text-white px-6 py-3.5 rounded-sm font-display font-semibold text-sm disabled:opacity-60"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Starting checkout…
                  </>
                ) : (
                  <>
                    Pay AED {total.toFixed(2)} · Ziina
                    <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Field = ({ label, name, value, onChange, placeholder, type = "text", testId }) => (
  <div>
    <label htmlFor={name} className="overline text-inkmute block mb-2">
      {label}
    </label>
    <input
      id={name}
      name={name}
      type={type}
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      data-testid={testId}
      className="w-full bg-surface border border-hairline focus:border-ink outline-none py-3 px-3 text-ink placeholder:text-inkmute/60 font-body text-base rounded-sm"
    />
  </div>
);
